<div class="page-shell page-shell--canvas">

  <form [formGroup]="form" (ngSubmit)="save()" class="dc-wrapper">
    <div class="dc-header">
      <div class="dc-header__start">
        <mat-form-field appearance="outline" class="dc-name-field">
          <mat-label>{{ "background.name" | translate }}</mat-label>
          <input matInput formControlName="name" />
        </mat-form-field>
      </div>
      <div class="dc-header__actions">
        <button mat-stroked-button type="button" (click)="back()">
          {{ "cancel" | translate }}
        </button>
        <button
          mat-raised-button
          type="button"
          color="primary"
          (click)="save()"
          [disabled]="saving || !canSave || !pageReady"
        >
          @if (saving) {
            <mat-spinner diameter="20" class="dc-btn-spinner"></mat-spinner>
          } @else {
            <mat-icon class="dc-btn-icon">save</mat-icon>
          }
          {{ saving ? ("saving" | translate) : ("save" | translate) }}
        </button>
      </div>
    </div>
    <div class="dc-editor-grid">
      <div class="dc-section dc-preview-panel dc-preview-panel--editor">
        <div class="dc-section__content dc-preview-panel__body">
          <h3 class="dc-preview-panel__title">
            {{ "background.previewTitle" | translate }}
          </h3>
          <div class="dc-employee-picker-wrap dc-editor-preview-employee-picker">
            <app-digital-card-employee-picker
              [value]="previewEmployee"
              [templateContextId]="pickerTemplateContextId"
              (valueChange)="onPreviewEmployeeChange($event)"
              (initialSelectionResolved)="onEmployeePickerInitialResolved()"
            ></app-digital-card-employee-picker>
          </div>
          <div class="dc-preview-panel__stage">
            <app-background-preview
              [bannerType]="form.get('bannerType')?.value ?? 'gradient'"
              [bannerGradientStart]="form.get('bannerGradientStart')?.value ?? '#ffffff'"
              [bannerGradientEnd]="form.get('bannerGradientEnd')?.value ?? '#f5f5f0'"
              [bannerImageUrl]="bannerPreviewUrl"
              [showLogo]="form.get('showLogo')?.value ?? true"
              [hasQr]="!!form.get('digitalCardProfileId')?.value"
              [logoUrl]="companyLogoUrl"
              [qrLabel]="form.get('qrLabel')?.value ?? ''"
              [qrData]="previewQrTarget"
              [fontFamily]="previewFontFamily"
              [fontCssUrl]="previewFontCssUrl"
              [companyFontFileUrl]="previewCompanyFontFileUrl"
              [loading]="loadingPreview && !!form.get('digitalCardProfileId')?.value"
            ></app-background-preview>
          </div>
        </div>
      </div>

      <div class="dc-section dc-form-panel">
        <div class="dc-section__content">
          <div class="dc-form">
            <section class="dc-block">
              <header class="dc-block__head">
                <h3 class="dc-block__title">
                  {{ "background.fontSectionTitle" | translate }}
                </h3>
              </header>
              <div class="dc-block__body">
                <mat-form-field appearance="outline" class="dc-form-field-full">
                  <mat-label>{{ "background.fontLabel" | translate }}</mat-label>
                  <mat-select formControlName="fontPreset">
                    @for (font of fontPresets; track font.id) {
                      <mat-option [value]="font.id">
                        {{ font.label }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
                @if (!companyBrandFontReady) {
                  <p class="dc-font-upload-hint">
                    {{ "background.companyFontMissing" | translate
                    }}<a routerLink="/settings/company">{{
                      "background.companyFontSettingsLink" | translate
                    }}</a>
                  </p>
                }
              </div>
            </section>

            <section class="dc-block">
              <header class="dc-block__head">
                <h3 class="dc-block__title">
                  {{ "digitalCards.addImages" | translate }}
                </h3>
              </header>
              <div class="dc-block__body">
                <mat-form-field appearance="outline" class="dc-form-field-full">
                  <mat-label>{{ "digitalCard.settings.bannerType" | translate }}</mat-label>
                  <mat-select formControlName="bannerType">
                    <mat-option value="gradient">{{
                      "digitalCard.settings.bannerGradient" | translate
                    }}</mat-option>
                    <mat-option value="image">{{
                      "digitalCard.settings.bannerImage" | translate
                    }}</mat-option>
                  </mat-select>
                </mat-form-field>

                @if (form.get("bannerType")?.value === "gradient") {
                  <div class="dc-color-grid">
                    <app-print-color
                      [label]="'digitalCard.settings.gradientStart' | translate"
                      [value]="form.get('bannerGradientStart')?.value || '#ffffff'"
                      [showCmyk]="false"
                      (valueChange)="onGradientColorChange('bannerGradientStart', $event)"
                    ></app-print-color>
                    <app-print-color
                      [label]="'digitalCard.settings.gradientEnd' | translate"
                      [value]="form.get('bannerGradientEnd')?.value || '#f5f5f0'"
                      [showCmyk]="false"
                      (valueChange)="onGradientColorChange('bannerGradientEnd', $event)"
                    ></app-print-color>
                  </div>
                }

                @if (form.get("bannerType")?.value === "image") {
                  <div class="dc-upload">
                    <span class="dc-upload__label">{{
                      "digitalCard.settings.bannerImageUpload" | translate
                    }}</span>
                    <app-image-uploader
                      [previewUrl]="bannerPreviewUrl"
                      [uploadFn]="uploadBanner"
                      (onFileChange)="onBannerFileChange($event)"
                      (onFileRemove)="removeBanner()"
                    ></app-image-uploader>
                  </div>
                }
              </div>
            </section>

            <section class="dc-block">
              <header class="dc-block__head">
                <h3 class="dc-block__title">
                  {{ "digitalCards.visibleOnCard" | translate }}
                </h3>
              </header>
              <div class="dc-block__body dc-block__body--toggles">
                <div class="dc-toggle-list">
                  <div class="dc-toggle-item">
                    <mat-slide-toggle
                      color="primary"
                      formControlName="showLogo"
                      class="dc-toggle-item__control"
                    >
                      {{ "background.showLogo" | translate }}
                    </mat-slide-toggle>
                  </div>
                </div>
              </div>
            </section>

            <section class="dc-block">
              <header class="dc-block__head">
                <h3 class="dc-block__title">
                  {{ "background.qrDigitalCard" | translate }}
                </h3>
              </header>
              <div class="dc-block__body">
                <app-searchable-select
                  class="dc-form-field-full"
                  formControlName="digitalCardProfileId"
                  [label]="'background.qrDigitalCard' | translate"
                  [options]="digitalCardTemplates"
                  [displayWith]="digitalCardTemplateLabel"
                  [valueWith]="digitalCardTemplateValue"
                  [emptyLabel]="'background.none' | translate"
                  [emptyValue]="null"
                  (valueChange)="onDigitalCardProfileChange()"
                ></app-searchable-select>
                @if (form.get("digitalCardProfileId")?.value) {
                  <mat-form-field appearance="outline" class="dc-form-field-full">
                    <mat-label>{{ "background.qrLabelText" | translate }}</mat-label>
                    <input matInput formControlName="qrLabel" />
                  </mat-form-field>
                }
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  </form>
@if (!pageReady) {
  <div class="dbc1-loading dbc1-loading--overlay">
    <app-loading></app-loading>
  </div>
}
</div>
